/* eslint-disable max-lines */

import { fetchReviewers, setCommentError } from 'actions';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getSingleEnvelopeComments } from 'selectors/envelopes';
import { useSelector } from 'store';

import { CommentWithUsers, EnvelopeAction } from 'types';
import AddCommentSection from './AddCommentSection';
import MemoizedCommentsList from './CommentsList';

type ComponentProps = {
  setComment: (comment: CommentWithUsers) => void;
  comment: CommentWithUsers;
  handleSaveComment: (value?: string) => void;
  onCancelComment?: () => void;
  commentsFilter?: (c: EnvelopeAction) => boolean;
  scrollOnError?: boolean;
  referenceText?: string;
  focusOnMount?: boolean;
};

const CommentsSection: React.FC<ComponentProps> = ({
  setComment,
  comment,
  commentsFilter,
  onCancelComment,
  handleSaveComment,
  scrollOnError = false,
  referenceText,
  focusOnMount,
}) => {
  const dispatch = useDispatch();
  const { envelopeId } = useParams<{ envelopeId: string }>();

  const unfilteredComments = useSelector((state) => getSingleEnvelopeComments(state, envelopeId));
  const comments = useMemo(() => {
    if (commentsFilter) {
      return unfilteredComments.filter(commentsFilter);
    }
    return unfilteredComments;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unfilteredComments, commentsFilter]);

  const [editingId, setEditingId] = useState('');

  useEffect(() => {
    dispatch(fetchReviewers({ searchValue: '', limit: -1 }));
  }, [dispatch]);

  useEffect(() => {
    if (comments.length > 0) {
      dispatch(setCommentError(false));
    }
  }, [comments.length, dispatch]);

  useEffect(() => {
    dispatch(setCommentError(false));
  }, [envelopeId, dispatch]);

  return (
    <div className="flex flex-col gap-4 bg-white">
      {referenceText && <p className="px-4">&ldquo;{referenceText}&rdquo;</p>}
      <div className="flex flex-col gap-3 bg-white">
        <AddCommentSection
          setComment={setComment}
          comment={comment}
          handleSaveComment={handleSaveComment}
          onCancelComment={onCancelComment}
          isEditing={!!editingId}
          scrollOnError={scrollOnError}
          focusOnMount={focusOnMount}
        />

        <MemoizedCommentsList
          setEditingId={setEditingId}
          editingId={editingId}
          comments={comments}
        />
      </div>
    </div>
  );
};

export default CommentsSection;
